import React, {
    useEffect,
    useState,
    useMemo
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from "react-router";
import "webcomponent-qr-code";

import config from "../config/config";
import * as UTILS from "../utils/utils";
import * as QUERIES from "../queries/query-functions";

import { getCustomer } from "../queries/global";

import LoadingIndicator from '../components/loading-indicator';
import AccountNavigation from './account-navigation';
import UpsellBlock from '../components/upsell-block';
import LogoutButton from '../components/logout-button';
import Download from '../svg/download';

import AppleWalletSvg from '../svg/apple-wallet';
import GoogleWalletSvg from '../svg/google-wallet';

const AccountTickets = () => {

    const { data: customer } = getCustomer();

    const params = useParams();
    const [tickets, setTickets] = useState(null);

    const { 
        isPending: ordersPending,
        data: ordersData,
    } = useQuery({
        queryKey: ['ordersData'],
        queryFn: QUERIES.getOrders,
        refetchOnWindowFocus: false,
        retry: false
    });

    useEffect(() => {
        if (!ordersData) return;

        const upcoming = UTILS.getUpcomingTickets(ordersData);

        if (params.instanceId) {
            const upcoming_by_instance = UTILS.getTicketsByInstance(upcoming, params.instanceId);
            setTickets(upcoming_by_instance);
        } else {
            setTickets(upcoming);
        }

    }, [ordersData]);

    if (ordersPending) return <LoadingIndicator type="page" />

    //function to generate e-tickets link with queries function
    const generateETicketsPDF = () => {
        if (!tickets || tickets.length === 0) return '';

        const instanceId = tickets[0].instance.id;
        const customerId = customer.id;

        QUERIES.getETicketsPDF(customerId, instanceId).then(blob => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'tickets.pdf';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        });
    };

    return (
        <>
            <AccountNavigation />

            <div className='booking-pathway__container booking-pathway__account booking-pathway__account--tickets'>

                <div className='booking-pathway__main-content'>

                    <div className='booking-pathway__tickets-header'>
                        <h1>Tickets</h1>
                        <button type="button" className='btn btn--logout' onClick={generateETicketsPDF}>Download Tickets 
                            <Download />
                        </button>
                    </div>
                    

                    <Link to='/account/orders' className='booking-pathway__breadcrumb'>Back to Orders & tickets</Link>
                    
                    <div className='printable-ticket__list grid'>
                        {tickets?.map((ticket) => 
                            <PrintableTicket key={ticket.id} ticket={ticket} />
                        )}
                    </div>
                    <div className='printable-ticket__list grid'>
                         {tickets && tickets.length > 0 && config.WALLET_NAME &&
                            <SaveToWallet ticket={tickets[0]} />
                        }
                    </div>
                </div>

                <div className='booking-pathway__footer'>
                    <LogoutButton />

                    <UpsellBlock content={config.OPTION_FIELDS.account?.orders_upsell} type="orders-upsell" />
                </div>

            </div>
        </>
    );
};

const PrintableTicket = (props) => {
    const formattedDate = useMemo(() => {
        return UTILS.formatDateTime(props.ticket.instance?.start, true, true)
    }, [props.ticket.instance?.start]);
    
    const { 
        isPending,
        isFetching,
        data: imageData,
    } = useQuery({
        queryKey: ['imageData', props.ticket.instance.event.id],
        queryFn: () => QUERIES.getEventImage(props.ticket.instance.event.id),
        refetchOnWindowFocus: false,
        retry: false,
        staleTime: 86400000
    });
    
    return (
        <div className='ticket-border'>
            <article className='printable-ticket'>
                <header className='printable-ticket__header'>
                    <div className='printable-ticket__image'>
                        {isPending || isFetching ?
                            <LoadingIndicator />
                        : imageData ?
                            <div dangerouslySetInnerHTML={{__html: imageData}}></div>
                        : config.LOGO_URLS?.site ?
                            <img src={config.LOGO_URLS?.site} />
                        : null
                        }
                    </div>
                    <div className='printable-ticket__qr-code'>
                        <p className='sr-only'>QR Code</p>
                        <qr-code
                            data={ props.ticket.barcode }
                            format="svg"
                            margin="0"
                            modulesize="0"
                        />
                    </div>

                    <h2 className='printable-ticket__title'>{props.ticket.instance.event.name}</h2>
                </header>

                <div className='printable-ticket__content'>
                    {formattedDate ?
                        <time className='printable-ticket__info printable-ticket__info--date' dateTime={props.ticket.instance.start}>
                            <span>{formattedDate.date}</span>
                            <span>{formattedDate.time}</span>
                        </time>
                    :null}
                    <p className='printable-ticket__info printable-ticket__info--price'>
                        <span>{props.ticket.ticketType?.name}</span>
                        <span className='printable-ticket__info--price--total'>£{props.ticket.total?.toFixed(2)}</span>
                    </p>
                    <p className='printable-ticket__info'>
                        <span>Order No.</span>
                        <span>{props.ticket.orderId}</span>
                    </p>
                </div>

                <footer className='printable-ticket__footer'>
                    <p className='printable-ticket__seat-info'>
                        <span className="sr-only">Seat info:</span>
                        {props.ticket.seat?.attribute_Level ?
                            <span>{props.ticket.seat.attribute_Level}</span>
                        :null}
                        <span>{props.ticket.planName}</span>
                        <span>{props.ticket.seat?.name}</span>
                    </p>

                    {props.ticket.instance.event[`attribute_${config.OPTION_FIELDS.venue_attribute_name}`] && config.LOGO_URLS[props.ticket.instance.event[`attribute_${config.OPTION_FIELDS.venue_attribute_name}`]] ?
                        <img src={config.LOGO_URLS[props.ticket.instance.event[`attribute_${config.OPTION_FIELDS.venue_attribute_name}`]]} />
                    : null}
                </footer>
            </article>
        </div>
    )
}


const SaveToWallet  = (props) => {  
    const { ticket } = props;
    const google_wallet_url = `https://app.tickets-and-passes.com/api/GooglePass?client=${config.WALLET_NAME}&barcode=${ticket.barcode}`;
    const apple_wallet_url = `https://app.tickets-and-passes.com/api/ApplePass?client=${config.WALLET_NAME}&barcode=${ticket.barcode}`;

    return (
        <div className='save-to-wallet'>
            <a href={google_wallet_url} target="_blank" rel="noopener noreferrer">
                <GoogleWalletSvg />
                <span className="sr-only">Save to Google Wallet</span>
            </a>

            <a href={apple_wallet_url} target="_blank" rel="noopener noreferrer">
                   <AppleWalletSvg />
                <span className="sr-only">Save to Apple Wallet</span>
            </a>
        </div>

    )
}
export default AccountTickets;
